import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { UzSwitch, cn } from '@utazzo/ui';
import { ChevronDown, Copy, GripVertical, Trash2 } from 'lucide-react';
import React from 'react';

import { WELCOME_SECTION_META, sectionSummary } from './section-forms';

import type { WelcomeSection } from '@utazzo/shared';

/**
 * One draggable section in the Welcome editor - same accordion card as the
 * Website CMS page editor's `SortableSectionCard` (grip, number, type,
 * summary, visible switch, delete, chevron), plus Duplicate. The header
 * toggles the body, which renders the section's form inline.
 */
export const SectionRow = ({
  section,
  index,
  open,
  onToggleOpen,
  onToggleVisible,
  onDuplicate,
  onDelete,
  children,
}: {
  section: WelcomeSection;
  index: number;
  open: boolean;
  onToggleOpen: () => void;
  onToggleVisible: () => void;
  onDuplicate: () => void;
  onDelete: () => void;
  children: React.ReactNode;
}): React.JSX.Element => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: section.id,
  });
  const meta = WELCOME_SECTION_META[section.type];
  const Icon = meta.icon;
  const summary = sectionSummary(section);

  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={cn(
        'rounded-lg border bg-(--bg)',
        open ? 'border-(--border-strong) shadow-sm' : 'border-(--border)',
        isDragging && 'relative z-10 opacity-60 shadow-md',
      )}
    >
      <div className="flex items-center gap-1.5 px-2 py-2.5 sm:gap-2 sm:px-2.5">
        <button
          type="button"
          className="shrink-0 cursor-grab touch-none text-(--fg-faint) hover:text-(--fg) active:cursor-grabbing"
          aria-label="Drag to reorder"
          {...attributes}
          {...listeners}
        >
          <GripVertical className="h-4 w-4" />
        </button>
        <span className="hidden w-5 shrink-0 text-center text-[10.5px] font-medium text-(--fg-faint) tabular-nums sm:inline">
          {String(index + 1).padStart(2, '0')}
        </span>
        <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-(--z-100) text-(--fg-muted)">
          <Icon className="h-3.5 w-3.5" />
        </span>
        <button
          type="button"
          className={cn(
            'flex min-w-0 flex-1 flex-col text-left sm:flex-row sm:items-center sm:gap-2',
            !section.visible && 'opacity-60',
          )}
          onClick={onToggleOpen}
        >
          <span className="truncate text-sm font-medium text-(--fg) sm:shrink-0">{meta.label}</span>
          {summary && <span className="truncate text-xs text-(--fg-faint)">{summary}</span>}
        </button>
        <UzSwitch
          checked={section.visible}
          onCheckedChange={onToggleVisible}
          aria-label="Visible"
        />
        <button
          type="button"
          className="shrink-0 text-(--fg-faint) hover:text-(--fg)"
          onClick={onDuplicate}
          aria-label="Duplicate section"
          title="Duplicate"
        >
          <Copy className="h-4 w-4" />
        </button>
        <button
          type="button"
          className="shrink-0 text-(--fg-faint) hover:text-(--ds-error)"
          onClick={onDelete}
          aria-label="Delete section"
          title="Delete"
        >
          <Trash2 className="h-4 w-4" />
        </button>
        <button
          type="button"
          onClick={onToggleOpen}
          className="shrink-0 text-(--fg-faint)"
          aria-label={open ? 'Collapse' : 'Expand'}
          aria-expanded={open}
        >
          <ChevronDown
            className="h-4 w-4 transition-transform"
            style={{ transform: open ? 'rotate(180deg)' : 'none' }}
          />
        </button>
      </div>

      {open && (
        <div className="grid gap-4 border-t border-(--border) px-3 py-4 sm:px-4">{children}</div>
      )}
    </div>
  );
};
